<template>
    <div class="card p-fluid">
        <div class="field grid">
            <label for="type" class="col-12 mb-2 md:col-2 md:mb-0">Element Type</label>
            <div class="col-12 md:col-10">
                <Dropdown v-model="state.form.type" :options="getAllContainerTypes()" optionLabel="name" optionValue="value" placeholder="Select Element Type" showClear />
            </div>
        </div>

        <transition-group>
            <div v-for="field in filteredFields" :key="field.name" class="field grid">
                <label :for="field.name" class="col-12 mb-2 md:col-2 md:mb-0">{{ field.label }}</label>
                <div class="col-12 md:col-10">
                    <InputText
                        maxlength = "255"
                        :class="{ 'p-invalid': state.submitted && !state.form[field.name] }"
                        v-if="field.fieldType == AttributeInputTypes.INPUT_TEXT"
                        :type="field.type"
                        v-model="state.form[field.name]"
                        :placeholder="field.placeholder"
                        :aria-placeholder="field.placeholder"
                    />
                    <InputNumber v-else-if="field.fieldType == AttributeInputTypes.NUMBER" v-model="state.form[field.name]" :placeholder="field.placeholder" :useGrouping="false" :minFractionDigits="2" :maxFractionDigits="2" />
                    <Textarea v-else-if="field.fieldType == AttributeInputTypes.LONG_INPUT_TEXT" :rows="field.name == 'content' ? 5 : 3" v-model="state.form[field.name]" :placeholder="field.placeholder" :aria-placeholder="field.placeholder" />
                    <Dropdown
                        :class="{ 'p-invalid': state.submitted && !state.form[field.name] && field.name == 'parent_id' }"
                        v-else-if="field.fieldType == AttributeInputTypes.DROP_DOWN"
                        v-model="state.form[field.name]"
                        :optionLabel="field.name == 'formula_id' ? 'code' : 'name'"
                        :optionValue="field.name == 'formula_id' ? 'id' : 'value'"
                        :options="field.options"
                        :placeholder="field.placeholder"
                        :aria-placeholder="field.placeholder"
                        showClear
                        :filter="!field.optionLabel ? true : false"
                    />
                    <custom-file-uploader
                        v-else-if="field.fieldType == 'ImageUpload'"
                        :refId="state.form['parent_id'] + 'attachment'"
                        :uploadFile="uploadFile"
                        @files-uploaded="fileUploaded"
                        @files-empty="filesEmpty"
                        @image-selected="setSelectedImage"
                    />
                </div>
                <div class="mt-3 mx-auto" v-if="state.form.type == ContainerTypes.TEXT && field.name == ContainerTypes.TEXT">
                    <Button class="p-button-warning" label="Attach Attributes" @click="toggleAttributesDialog" />
                </div>
            </div>
        </transition-group>
        <div v-if="state.form.type" class="btns-container col-6 mx-auto">
            <Button :label="'Add ' + state.form.type" class="p-button-raised" @click="addElements" />
            <!-- <Button label="Create Box" class="p-button-raised p-button-warning" /> -->
        </div>
        <!-- Overlay Panel to display attributes list -->
        <block-attributes-overlay-panel :toggle="toggleAttributes" :selectedBlockId="selectedBlockId" @blockAttributesSet="setBlockAttributes" />
    </div>
</template>

<script setup>
import CustomFileUploader from '../files/CustomFileUploader.vue';
import BlockAttributesOverlayPanel from './BlockAttributesOverlayPanel.vue';
import { ref, reactive, computed, onMounted } from 'vue';
import { showToast } from '@/helpers/toast-handler';
import useBoxBuilderComposable from '@/composables/BoxBuilderComposable';
import { AttributeInputTypes, ContainerTypes } from '../../enums';
import { getAllContainerTypes, getAllDynamicFields } from '@/helpers/enum-helpers';

// using block to extract block attributes
const { formulas, getAllFonts, fonts, parentTemplates, selectedBlockId, submitResultTemplate } = useBoxBuilderComposable();
const blockAttributes = ref([]);
const toggleAttributes = ref();
const uploadFile = ref(false);

onMounted(async () => {
    await getAllFonts();
});

const setBlockAttributes = (attributes) => {
    blockAttributes.value = attributes;
};

const toggleAttributesDialog = (event) => {
    toggleAttributes.value = event;
};

const fileUploaded = async (fileIds) => {
    // fileUploads are made in a way to handle multiple file uploads
    // so it will send an array, select the first index only in here

    state.form.details ? (state.submitData.details['attachment'] = fileIds[0]) : (state.submitData.details = { attachment: fileIds[0] });
    // after image has been uploaded, submit the form
    await submit();
};

const setSelectedImage = (id) => {
    state.form.attachment = id;
};

const initialFormState = () => {
    return {
        name: null,
        description: null,
        type: null,
        layout: null,
        parent_id: null,
        formula_id: null,
        position: 1,
        font: null,
        fontSize: null,
        valign: null,
        halign: null,
        // margin: null,
        content: null,
        attachment: null,
    };
};
const createInitialState = () => {
    return reactive({
        form: initialFormState(),
        dynamicFields: getAllDynamicFields(formulas, fonts, parentTemplates),
        details: {},
        submitData: {},
        submitted: false,
    });
};
const state = createInitialState();

const filteredFields = computed(() => {
    return state.dynamicFields.filter((field) => field.types?.includes(state.form.type) || !field.types);
});

const addElements = async () => {
    state.submitted = true;
    if (isValid()) {
        state.submitData = formatElementsJson();
        // check if image type and image not selected as well
        if (state.submitData.type == 'image' && !state.submitData.details?.attachment) {
            // trigger image upload
            uploadFile.value = true;
        } else {
            await submit();
        }
    }
};

const submit = async () => {
    await submitResultTemplate(state.submitData).then((data) => {
        // if element has been created then flush the form state
        if (data?.id) {
            state.form = initialFormState();
            state.details = {};
            state.submitData = {};
            state.submitted = false;
        }
    });
};

const formatElementsJson = () => {
    let temp = {};
    for (const key in state.form) {
        if (key !== 'company_id' && key !== 'name' && key !== 'description' && key !== 'type' && key !== 'parent_id' && key !== 'position' && key !== 'formula_id') {
            if (state.form[key] !== null) {
                state.details[key] = state.form[key];
            }
        }
    }
    temp = {
        name: state.form.name,
        description: state.form.description,
        type: state.form.type,
        parent_id: state.form.parent_id,
        position: state.form.position,
        formula_id: state.form.formula_id,
    };
    // put variables key if its a text type
    // add others here later when they are added
    if (state.form.type == 'text') {
        state.details['variables'] = blockAttributes.value?.reduce((obj, item) => {
            obj[item.code] = item.variable;
            return obj;
        }, {});
    }

    if (Object.keys(state.details).length > 0) {
        temp.details = state.details;
    }
    return temp;
};

const isValid = () => {
    if (!state.form.name) {
        showToast({}, 'Element Name cannot be empty');
        return false;
    }
    // Checks if its not cover types or main types
    const validTypes = [ContainerTypes.CONTAINER, ContainerTypes.TEXT, ContainerTypes.OL, ContainerTypes.UL, ContainerTypes.IMAGE];
    const textTypes = [ContainerTypes.TEXT, ContainerTypes.OL, ContainerTypes.UL];
    if (validTypes.includes(state.form.type)) {
        if (!state.form.parent_id) {
            showToast({}, 'Select a parent element first');
            return false;
        }
        if (textTypes.includes(state.form.type)) {
            if (!state.form.text) {
                showToast({}, 'Text Element cannot be empty. Input content value');
                return false;
            }
        }
    }
    return true;
};

const filesEmpty = () => {
    showToast({}, 'Image Upload field cannot be empty');
};
</script>
